import { useEffect, useRef } from 'react';

import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { EarlyAccessRing, EarlyAccessRingApi } from 'apis/EarlyAccessRingApi';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { helpContent } from 'pages/account/feature_ring/help';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FeatureFlagApi, User } from 'apis/FeatureFlagApi';
import { StudentPermissions } from 'pages/students/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { PermissionsApi, UsersApi } from 'apis';
import { KendoIntlProvider } from 'app/culture';
import { GMetrixTable } from 'components';
import { useSuperState } from 'hooks';

import 'pages/account/feature_ring/styles/FeatureRingManageUser.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

const matchesSearch = (user: User, term: string) => {

    const t = term.toLowerCase();
    return (
        user.firstname?.toLowerCase().includes(t) ||
        user.lastname?.toLowerCase().includes(t) ||
        user.username?.toLowerCase().includes(t) ||
        user.RingName?.toLowerCase().includes(t)
    );

};

export const FeatureRingManageUser = () => {

    const { formatMessage, locale } = useIntl();
    const { catchWithToast, createToast } = useNotification();

    const defaultRing: EarlyAccessRing = {
        Description: formatMessage({ id: 'features_in_this_ring_are_available_syst.d133d842', defaultMessage: 'Features in this ring are available system-wide, but the old feature can still be accessed.' }),
        Id: 0,
        Name: formatMessage({ id: 'public.17627c48', defaultMessage: 'Public' })
    };

    const [state, setState] = useSuperState({
        addSearchTerm: '',
        addSearchTermDebounced: '',
        addUsers: [] as User[],
        currentEarlyAccessRing: defaultRing,
        earlyAccessRings: [] as EarlyAccessRing[],
        editRing: defaultRing,
        editUser: null as unknown as User,
        loadingGridData: false,
        loadingPermissions: true,
        permissions: null as unknown as StudentPermissions,
        searchTerm: '',
        selectedAddUsers: [] as User[],
        showEditModal: false,
        showModal: false,
        users: [] as User[]
    });
    const {
        addSearchTerm, addSearchTermDebounced, addUsers, currentEarlyAccessRing, earlyAccessRings, editRing,
        editUser, loadingGridData, loadingPermissions, searchTerm, selectedAddUsers, showEditModal, showModal, users
    } = state;

    const addSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchInviteOnlyUsers = async (): Promise<User[]> => {

        const res = await FeatureFlagApi.GetAllInviteOnlyUsers();
        res.forEach(user => {

            user.Id = user.id;

        });
        return res;

    };

    const reloadUsers = async () => {

        try {

            const res = await fetchInviteOnlyUsers();
            setState({ users: res });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_users.95f1b1e7', defaultMessage: 'Failed to load users.' }))(e);

        }

    };

    const loadDialogUsers = async () => {

        try {

            setState({ loadingGridData: true });
            const res = await UsersApi.GetAll();
            const mapped: User[] = res.map(u => ({
                id: u.UserProfileID,
                firstname: u.FirstName,
                Id: u.UserProfileID,
                lastname: u.LastName,
                LoginId: u.Id,
                RingId: u.FeatureRingId,
                RingName: '',
                username: u.UserName
            }));
            setState({ addUsers: mapped });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_users.95f1b1e7', defaultMessage: 'Failed to load users.' }))(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const applyFeatureFlag = async (loginIds: string[], ringId: number, successMsg: string, failureMsg: string) => {

        const result = await FeatureFlagApi.SetFeatureFlagUsers(loginIds, ringId);
        const ok = result.Done;
        createToast(result.Message || (ok ? successMsg : failureMsg), ok ? 'success' : 'danger');

    };

    const openEditModal = (user: User) => {

        const matchingRing = earlyAccessRings.find(r => r.Id === user.RingId) || defaultRing;
        setState({ editRing: matchingRing, editUser: user, showEditModal: true });

    };

    const saveChanges = async () => {

        if (!editUser) return;

        try {

            await applyFeatureFlag(
                [editUser.LoginId],
                editRing.Id,
                formatMessage({ id: 'changes_saved_successfully.d0bda37d', defaultMessage: 'Changes saved successfully.' }),
                formatMessage({ id: 'failed_to_save_changes.2be76168', defaultMessage: 'Failed to save changes.' })
            );
            await reloadUsers();
            setState({ editRing: defaultRing, editUser: undefined, showEditModal: false });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_save_changes.2be76168', defaultMessage: 'Failed to save changes.' }))(e);

        }

    };

    const addUsersToBeta = async () => {

        setState({ showModal: false });
        const loginIds = selectedAddUsers.map(u => u.LoginId).filter(id => id !== undefined && id !== null);

        if (loginIds.length > 0) {

            try {

                await applyFeatureFlag(
                    loginIds,
                    currentEarlyAccessRing.Id,
                    formatMessage({ id: 'users_added_successfully.1922e861', defaultMessage: 'Users added successfully.' }),
                    formatMessage({ id: 'failed_to_add_users.29104852', defaultMessage: 'Failed to add users.' })
                );
                await reloadUsers();

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_add_users.29104852', defaultMessage: 'Failed to add users.' }))(e);

            }

        }

        setState({ addSearchTerm: '', addSearchTermDebounced: '', addUsers: [], selectedAddUsers: [] });

    };

    const handleShowModal = () => setState({ showModal: true });

    // Debounce the dialog search term (300ms).
    useEffect(() => {

        if (addSearchDebounceRef.current) clearTimeout(addSearchDebounceRef.current);
        addSearchDebounceRef.current = setTimeout(() => {

            setState({ addSearchTermDebounced: addSearchTerm });

        }, 1000);

        return () => {

            if (addSearchDebounceRef.current) clearTimeout(addSearchDebounceRef.current);

        };

    }, [addSearchTerm]);

    // Lazy-load invite-only users only after the user has typed a search term.
    useEffect(() => {

        if (showModal && addSearchTermDebounced.length > 0 && addUsers.length === 0 && !loadingGridData) {

            loadDialogUsers();

        }

    }, [showModal, addSearchTermDebounced]);

    useEffect(() => {

        const init = async () => {

            try {

                const perms = await PermissionsApi.Get('FeatureRing');
                setState({ permissions: perms });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_permissions.ab18656f', defaultMessage: 'Failed to load permissions.' }))(e);

            }

            await reloadUsers();

            try {

                const rings = await EarlyAccessRingApi.GetAll();
                setState({ earlyAccessRings: rings });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_feature_rings.4fda1a87', defaultMessage: 'Failed to load feature rings.' }))(e);

            }

            setState({ loadingPermissions: false });

        };

        init();

    }, []);

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'manage_user_access.3ee26442', defaultMessage: 'Manage User Access' })}>
            {loadingPermissions ?
                (
                    <div className={'d-flex align-items-center justify-content-center h-100'}>
                        <GMetrixLoader />
                    </div>
                )
                :
                (
                    <KendoIntlProvider>
                        <LocalizationProvider language={locale}>
                            <IntlProvider locale={locale}>

                                <div className="d-flex flex-column gap-3">

                                    <PageHeader
                                        cta={{
                                            label: formatMessage({ id: 'add_user_to_early_access.06096edb', defaultMessage: 'Add User to Early Access' }),
                                            onClick: handleShowModal
                                        }}
                                        helpContent={helpContent}
                                        title={formatMessage({ id: 'manage_user_access.3ee26442', defaultMessage: 'Manage User Access' })}
                                    />

                                    <Card body>

                                        <GMetrixTable
                                            allowExport
                                            columnData={[
                                                { field: 'firstname', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                                                { field: 'lastname', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                                                { field: 'username' },
                                                { field: 'RingName', title: formatMessage({ id: 'ring_name.68663b23', defaultMessage: 'Ring Name' }) },
                                                {
                                                    cell: ({ dataItem }) => (
                                                        <td>
                                                            <GMetrixButton
                                                                iconClass="fa-regular fa-pencil"
                                                                onClick={() => openEditModal(dataItem)}
                                                                size="sm"
                                                                variant="outline-primary"
                                                            >
                                                                <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                                                            </GMetrixButton>
                                                        </td>
                                                    ),
                                                    title: formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' }),
                                                    width: 120
                                                }
                                            ]}
                                            bottomBarTableName={formatMessage({ id: 'users.581e673f', defaultMessage: 'Users' })}
                                            rowData={users.filter(u => matchesSearch(u, searchTerm))}
                                            searchTerm={searchTerm}
                                            setSearchTerm={val => setState({ searchTerm: val })}
                                            tableName={formatMessage({ id: 'featureringusers.137b94b9', defaultMessage: 'FeatureRingUsers' })}
                                        />
                                    </Card>
                                </div>

                                <GMetrixDialog
                                    actionButtonClick={addUsersToBeta}
                                    actionButtonDisabled={selectedAddUsers.length === 0}
                                    actionButtonText={formatMessage({ id: 'add_amount_users.97804143', defaultMessage: 'Add {amount} users' }, { amount: selectedAddUsers.length })}
                                    dialogLabel={formatMessage({ id: 'add_user_to_early_access.06096edb', defaultMessage: 'Add User to Early Access' })}
                                    onCancel={() => setState({ addSearchTerm: '', addSearchTermDebounced: '', selectedAddUsers: [], showModal: false })}
                                    show={showModal}
                                    size={undefined}
                                >
                                    <div className="feature-ring-manage__dialog-panel mb-4">
                                        <div className="feature-ring-manage__dialog-step">
                                            <span className="feature-ring-manage__step-badge">
                                                1
                                            </span>
                                            <div className="feature-ring-manage__step-body">
                                                <GMetrixDropdown
                                                    dataItemKey="Id"
                                                    dataItems={earlyAccessRings}
                                                    label={formatMessage({ id: 'change_the_feature_ring.7bf6dee8', defaultMessage: 'Change the Feature Ring:' })}
                                                    onChange={val => setState({ currentEarlyAccessRing: val as EarlyAccessRing })}
                                                    textField="Name"
                                                    value={currentEarlyAccessRing}
                                                />
                                            </div>
                                        </div>
                                        <div className="feature-ring-manage__dialog-step">
                                            <span className="feature-ring-manage__step-badge">
                                                2
                                            </span>
                                            <div className="feature-ring-manage__step-body feature-ring-manage__hint">
                                                <FormattedMessage defaultMessage="for the users selected below" id="for_the_users_selected_below.2b1467b9" />
                                            </div>
                                        </div>
                                    </div>
                                    <GMetrixTable
                                        selectableRows
                                        columnData={[
                                            { field: 'firstname', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                                            { field: 'lastname', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                                            { field: 'username', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) }
                                        ]}
                                        bottomBarTableName={formatMessage({ id: 'users.581e673f', defaultMessage: 'Users' })}
                                        loading={loadingGridData}
                                        noDataMessage={formatMessage({ id: 'search_for_a_user_to_get_started.32021371', defaultMessage: 'Search for a user to get started' })}
                                        onSelectedChange={val => setState({ selectedAddUsers: val })}
                                        rowData={addSearchTermDebounced.length === 0 ? [] : addUsers.filter(u => matchesSearch(u, addSearchTermDebounced))}
                                        searchTerm={addSearchTerm}
                                        selected={selectedAddUsers}
                                        setSearchTerm={val => setState({ addSearchTerm: val })}
                                        tableName={formatMessage({ id: 'inviteonlyusers.2efb1ba9', defaultMessage: 'InviteOnlyUsers' })}
                                    />
                                </GMetrixDialog>

                                <GMetrixDialog
                                    actionButtonClick={saveChanges}
                                    actionButtonDisabled={!editUser}
                                    actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                                    dialogLabel={formatMessage({ id: 'change_the_feature_ring.7bf6dee8', defaultMessage: 'Change the Feature Ring:' })}
                                    onCancel={() => setState({ editRing: defaultRing, editUser: undefined, showEditModal: false })}
                                    show={showEditModal}
                                    size="sm"
                                >
                                    <Container fluid className="feature-ring-manage__dialog-toolbar mb-3">
                                        <Row className="align-items-center">
                                            <Col className="feature-ring-manage__label mb-2" xs={12}>
                                                {editUser ? `${editUser.firstname} ${editUser.lastname}` : ''}
                                            </Col>
                                            <Col xs={12}>
                                                <GMetrixDropdown
                                                    dataItemKey="Id"
                                                    dataItems={earlyAccessRings}
                                                    onChange={val => setState({ editRing: val as EarlyAccessRing })}
                                                    textField="Name"
                                                    value={editRing}
                                                />
                                            </Col>
                                        </Row>
                                    </Container>
                                </GMetrixDialog>

                            </IntlProvider>
                        </LocalizationProvider>
                    </KendoIntlProvider>
                )
            }
        </DefaultLayout>
    );

};
